import { useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const TestimonialForm = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    nome: '',
    cargo: '',
    texto: '',
    rating: 5
  });
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [mostrarFormulario, setMostrarFormulario] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setMensagem('Precisas de fazer login para enviar um testemunho.');
      return;
    }

    setLoading(true);
    setMensagem('');

    try {
      const { error } = await supabase
        .from('testemunhos')
        .insert([
          {
            user_id: user.id,
            nome: formData.nome,
            cargo: formData.cargo,
            texto: formData.texto,
            rating: formData.rating,
            status: 'pendente'
          }
        ]);

      if (error) throw error;

      setMensagem('✅ Testemunho enviado com sucesso! Será publicado após aprovação.');
      setFormData({ nome: '', cargo: '', texto: '', rating: 5 });
      setMostrarFormulario(false);
      
      setTimeout(() => setMensagem(''), 5000);
    } catch (error: any) {
      setMensagem('❌ Erro ao enviar testemunho. Tenta novamente.');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <section className="py-16 px-4 bg-gradient-to-br from-gray-900 to-black">
      <div className="max-w-3xl mx-auto">
        {!mostrarFormulario ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h3 className="text-3xl font-bold text-white mb-4">
              Partilha a Tua Experiência
            </h3>
            <p className="text-gray-400 mb-8">
              Ajuda outros a conhecer a nossa plataforma através da tua opinião
            </p>
            <button
              onClick={() => setMostrarFormulario(true)}
              className="px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-500 transition-all whitespace-nowrap"
            >
              Escrever Testemunho
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-black border border-gray-800 rounded-2xl p-8"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">
                Escreve o Teu Testemunho
              </h3>
              <button
                onClick={() => setMostrarFormulario(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-white font-medium mb-2">
                  Nome Completo
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  maxLength={100}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400 transition-colors"
                  placeholder="O teu nome"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  Cargo/Profissão
                </label>
                <input
                  type="text"
                  value={formData.cargo}
                  onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                  required
                  maxLength={100}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400 transition-colors"
                  placeholder="Ex: Marketing Digital"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  Avaliação
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="w-12 h-12 flex items-center justify-center transition-transform hover:scale-110"
                    >
                      <i
                        className={`ri-star-${formData.rating >= star ? 'fill' : 'line'} text-3xl ${
                          formData.rating >= star ? 'text-yellow-400' : 'text-gray-600'
                        }`}
                      ></i>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  O Teu Testemunho
                </label>
                <textarea
                  value={formData.texto}
                  onChange={(e) => setFormData({ ...formData, texto: e.target.value })}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400 transition-colors resize-none"
                  placeholder="Partilha a tua experiência connosco..."
                />
                <p className="text-gray-500 text-sm mt-2">
                  {formData.texto.length}/500 caracteres
                </p>
              </div>

              {mensagem && (
                <div className={`p-4 rounded-lg ${
                  mensagem.includes('✅') 
                    ? 'bg-green-900/30 border border-green-700 text-green-400' 
                    : 'bg-red-900/30 border border-red-700 text-red-400'
                }`}>
                  {mensagem}
                </div>
              )}

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {loading ? 'A Enviar...' : 'Enviar Testemunho'}
                </button>
                <button
                  type="button"
                  onClick={() => setMostrarFormulario(false)}
                  className="px-6 py-3 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-700 transition-all whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default TestimonialForm;